import { Component, OnInit, OnDestroy, PLATFORM_ID, Inject, Renderer2, NgZone } from '@angular/core';
import { ApiService } from '../../../services/api';

// ... (imports remain the same, just adding ApiService to imports if not already there, but since I cannot see imports here easily in a replace block without context, I will just add the import at the top in a separate replace call if needed, or assume I can't easily. Wait, I can't inject it if I don't import it. I'll use multi_replace to do both.)

// Actually, I'll use replace_file_content for the whole file or large chunks.
// Let's use multi_replace_file_content.

declare var DOMPurify: any;
import { CommonModule, isPlatformBrowser, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { BlogService } from '../../../services/blog.service';
import { BlogPost, BlogSchema } from '../../../models/blog.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-blog-post',
    standalone: true,
    imports: [CommonModule, RouterModule, DatePipe],
    templateUrl: './blog-post.component.html',
    styleUrls: ['./blog-post.component.scss']
})
export class BlogPostComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    post: BlogPost | null = null;
    related: BlogPost[] = [];
    relatedPosts: BlogPost[] = [];
    schema: BlogSchema | null = null;
    safeContent: SafeHtml | null = null; // Cache sanitized content

    loading = true;
    error = '';

    // Reading progress
    scrollProgress = 0;

    // Table of contents
    toc: { id: string; text: string; level: number }[] = [];
    showToc = false;

    // Interaction state
    liked = false;

    // Analytics tracking
    private startTime = 0;
    private maxScrollDepth = 0;
    private isBrowser = false;

    constructor(
        private route: ActivatedRoute,
        private blogService: BlogService,
        private apiService: ApiService,
        private meta: Meta,
        private title: Title,
        private sanitizer: DomSanitizer,
        private renderer: Renderer2,
        private ngZone: NgZone,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    private scrollHandler = () => {
        if (!this.scrollTicking) {
            requestAnimationFrame(() => {
                this.updateProgress();
                this.scrollTicking = false;
            });
            this.scrollTicking = true;
        }
    };
    private scrollTicking = false;

    ngOnInit(): void {
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const slug = params['slug'];
            if (slug) {
                this.loadPost(slug);
            }
        });

        if (this.isBrowser) {
            this.startTime = Date.now();
            this.ngZone.runOutsideAngular(() => {
                window.addEventListener('scroll', this.scrollHandler, { passive: true });
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.isBrowser) {
            window.removeEventListener('scroll', this.scrollHandler);
            this.trackAnalytics();
        }
    }

    // Direct DOM update to avoid Change Detection on every scroll frame
    updateProgress(): void {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;

        // Update progress bar directly
        const progressBar = document.getElementById('reading-progress-bar');
        if (progressBar) {
            progressBar.style.width = `${progress}%`;
        }

        // Track max scroll depth logic (keep loose track, don't trigger CD)
        if (progress > this.maxScrollDepth) {
            this.maxScrollDepth = progress;
        }
    }

    loadPost(slug: string): void {
        this.loading = true;
        this.error = '';

        const user = this.apiService.getUserData();
        const userId = user ? user.id : undefined;

        this.blogService.getPost(slug, userId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.post = response.data.post;
                    this.liked = !!(this.post && this.post.is_liked); // Set initial liked state
                    this.related = response.data.related;
                    this.relatedPosts = response.data.related || [];
                    this.schema = response.data.schema;

                    this.updateMetaTags();
                    this.injectSchema();

                    if (this.post && this.post.content) {
                        this.toc = this.blogService.generateTableOfContents(this.post.content);
                        this.safeContent = this.getSanitizedContent(this.post.content); // Sanitize once
                    }
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = 'Failed to load post';
                this.loading = false;

                // Set fallback SEO to prevent "Failed to load post" appearing in search results
                this.title.setTitle('Blog Post - QuickIndexing');
                this.meta.updateTag({ name: 'description', content: 'Read our latest blog posts about URL indexing, SEO strategies, and Google search optimization tips.' });
                this.meta.updateTag({ name: 'robots', content: 'noindex, follow' }); // Don't index error pages
            }
        });
    }

    updateMetaTags(): void {
        if (!this.post) return;

        this.title.setTitle(this.post.seo_title || this.post.title);

        this.meta.updateTag({ name: 'description', content: this.post.seo_description || this.post.excerpt });
        this.meta.updateTag({ name: 'robots', content: 'index, follow' }); // Ensure successful posts are indexable
        this.meta.updateTag({ property: 'og:title', content: this.post.title });
        this.meta.updateTag({ property: 'og:description', content: this.post.excerpt });
        this.meta.updateTag({ property: 'og:type', content: 'article' });

        if (this.post.featured_image?.large) {
            this.meta.updateTag({ property: 'og:image', content: this.post.featured_image.large });
        }

        // Canonical URL
        const link = document.querySelector('link[rel="canonical"]') || document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', window.location.href);
        document.head.appendChild(link);
    }

    injectSchema(): void {
        if (!this.schema || !this.isBrowser) return;

        // Remove existing schema
        const existing = document.querySelector('script[type="application/ld+json"]#blog-schema');
        if (existing) existing.remove();

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'blog-schema';
        script.textContent = JSON.stringify(this.schema);
        document.head.appendChild(script);
    }

    toggleLike(): void {
        if (!this.post) return;

        const user = this.apiService.getUserData();
        const userId = user ? user.id : undefined;

        // Add minimal animation classes logic if needed, but CSS active/focus state might be enough or we add a class

        this.blogService.toggleLike(this.post.id, userId).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.liked = response.data.liked;
                    if (this.post) {
                        this.post.likes += this.liked ? 1 : -1;
                    }
                }
            }
        });
    }

    trackAnalytics(): void {
        if (!this.post) return;

        const timeOnPage = Math.round((Date.now() - this.startTime) / 1000);

        this.blogService.trackAnalytics(this.post.id, {
            scroll_depth: Math.round(this.maxScrollDepth),
            time_on_page: timeOnPage,
            referrer: document.referrer || undefined,
            device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop'
        }).subscribe();
    }

    toggleToc(): void {
        this.showToc = !this.showToc;
    }

    scrollToSection(id: string): void {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        this.showToc = false;
    }

    share(platform: string): void {
        if (!this.post) return;

        const url = encodeURIComponent(window.location.href);
        const text = encodeURIComponent(this.post.title);

        let shareUrl = '';
        switch (platform) {
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`;
                break;
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                break;
        }

        if (shareUrl) {
            window.open(shareUrl, '_blank', 'width=600,height=400');
        }
    }



    private getSanitizedContent(content: string): SafeHtml {
        if (!content) return '';

        // Check if DOMPurify is available
        if (typeof DOMPurify !== 'undefined') {
            let clean = DOMPurify.sanitize(content, {
                ADD_TAGS: ['iframe', 'img'],
                ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'alt', 'class', 'style', 'loading']
            });
            // Responsive table wrapper
            clean = clean.replace(/<table/g, '<div class="table-container"><table').replace(/<\/table>/g, '</table></div>');
            return this.sanitizer.bypassSecurityTrustHtml(clean);
        }

        // Fallback or return as is (if trusted)
        if (this.isBrowser) {
            console.error('DOMPurify not loaded - Content blocked for security');
            return ''; // Safer to return empty than potentially unsafe content
        }

        return '';
    }

    getImageUrl(post: BlogPost): string {
        return post.featured_image?.medium || post.featured_image?.large || '/assets/blog-placeholder.jpg';
    }

    twitterShareUrl(): string {
        if (!this.post) return '#';
        const url = encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '');
        const text = encodeURIComponent(this.post.title);
        return `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    }

    linkedinShareUrl(): string {
        if (!this.post) return '#';
        const url = encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '');
        const title = encodeURIComponent(this.post.title);
        return `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`;
    }
}
